import React, { useCallback, useContext, useEffect, useState } from 'react';

import AccountPickerContext from 'contexts/AccountPickerContext';
import useTextCardContent from 'hooks/useTextCardContent';

import TextCardForm from './TextCardForm';

const EditTextCardForm = ({
  close,
  isOpen,
  textContentId,
}: {
  close: () => void;
  isOpen: boolean;
  textContentId: string;
}) => {
  const { accountRef } = useContext(AccountPickerContext);
  const {
    text: persistedText,
    isCenterAlign: persistedIsCenterAlign,
    isLoading,
  } = useTextCardContent(textContentId);

  const [text, setText] = useState<string>('');
  const [isCenterAlign, setIsCenterAlign] = useState<boolean>(false);

  useEffect(() => {
    if (!isOpen || isLoading) {
      return;
    }
    setText(persistedText);
    setIsCenterAlign(!!persistedIsCenterAlign);
  }, [isOpen, isLoading, persistedText, persistedIsCenterAlign]);

  const persist = useCallback(
    ({
      nextText,
      nextIsCenterAlign,
    }: {
      nextText: string;
      nextIsCenterAlign: boolean;
    }) => {
      if (process.env.NODE_ENV === 'test') {
        return;
      }

      accountRef
        .collection('textCardContents')
        .doc(textContentId)
        // Merged, keeping what Studio saved with it (its format and tile,
        // and its metrics, dates and filters)
        .set(
          { textContent: nextText, isCenterAlign: nextIsCenterAlign },
          { merge: true },
        );
    },
    [accountRef, textContentId],
  );

  const onChange = useCallback(
    (content: string) => {
      setText(content);
      persist({ nextText: content, nextIsCenterAlign: isCenterAlign });
    },
    [persist, isCenterAlign],
  );

  const onAlignChanged = useCallback(
    (newAlign: boolean) => {
      setIsCenterAlign(newAlign);
      persist({ nextText: text, nextIsCenterAlign: newAlign });
    },
    [persist, text],
  );

  return (
    <TextCardForm
      text={text}
      onChange={onChange}
      onSave={close}
      close={close}
      isOpen={isOpen}
      isCenterAlign={isCenterAlign}
      onAlignChanged={onAlignChanged}
    />
  );
};

export default EditTextCardForm;
